import { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * Hook pour sanitiser du HTML et se protéger contre les attaques XSS
 * Utilise DOMPurify pour nettoyer le HTML avant de l'afficher avec dangerouslySetInnerHTML
 * 
 * @param html - Chaîne HTML à sanitiser
 * @param options - Options de configuration DOMPurify optionnelles
 * @returns HTML sanitisé sûr à utiliser avec dangerouslySetInnerHTML
 */
export const useSanitizedHTML = (
  html: string, 
  options?: DOMPurify.Config
): string => {
  // Stringify options pour éviter les problèmes de référence d'objet
  const optionsKey = options ? JSON.stringify(options) : '';
  
  return useMemo(() => {
    const defaultConfig: DOMPurify.Config = {
      // Autorise les SVG et les filtres SVG
      USE_PROFILES: { html: true, svg: true, svgFilters: true },
      
      // Balises interdites (risques de sécurité)
      FORBID_TAGS: [
        'script',      // Scripts JavaScript
        'iframe',      // Frames externes
        'object',      // Objets embedés
        'embed',       // Media embedés
        'applet',      // Applets Java
        'link',        // Liens externes (CSS, etc.)
        'style',       // Styles inline (peut contenir JS)
        'form',        // Formulaires
        'input',       // Champs de saisie
        'button',      // Boutons
        'textarea',    // Zones de texte
        'select',      // Listes déroulantes
        'foreignObject' // Objets SVG étrangers
      ],
      
      // Attributs interdits (handlers d'événements)
      FORBID_ATTR: [
        'onerror', 'onload', 'onclick', 'onmouseover',
        'onmouseout', 'onmousemove', 'onmousedown', 'onmouseup',
        'onfocus', 'onblur', 'onchange', 'onsubmit',
        'onkeydown', 'onkeyup', 'onkeypress',
        'onanimationend', 'onanimationstart', 'onanimationiteration',
        'ontransitionend', 'ontransitionstart'
      ],
      
      // Pas d'attributs data-* personnalisés (peuvent être exploités)
      ALLOW_DATA_ATTR: false,
      
      // Pas d'URLs javascript:
      ALLOW_UNKNOWN_PROTOCOLS: false,
      
      // URLs autorisées (http/https seulement)
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    };

    // Fusionner avec les options personnalisées si fournies
    const config = options ? { ...defaultConfig, ...options } : defaultConfig;
    
    // Sanitiser le HTML
    return DOMPurify.sanitize(html, config);
  }, [html, optionsKey]);
};

/**
 * Hook spécialisé pour sanitiser des SVG (ex: QR codes)
 * Configuration optimisée pour les SVG tout en restant sécurisé
 */
export const useSanitizedSVG = (svg: string): string => {
  return useMemo(() => {
    return DOMPurify.sanitize(svg, {
      USE_PROFILES: { svg: true, svgFilters: true },
      FORBID_TAGS: ['script', 'foreignObject', 'use'],
      FORBID_ATTR: [
        'onerror', 'onload', 'onclick', 'onmouseover',
        'onfocus', 'onblur', 'onanimationend'
      ],
      ALLOW_DATA_ATTR: false,
      ADD_TAGS: ['svg', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'g', 'defs'],
    });
  }, [svg]);
};

/**
 * Hook pour sanitiser du texte simple (supprime tous les tags HTML)
 * Utile pour afficher du texte brut sans formatage
 */
export const useSanitizedText = (text: string): string => {
  return useMemo(() => {
    return DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [], // Aucune balise HTML autorisée
      KEEP_CONTENT: true, // Garder le contenu texte
    });
  }, [text]);
};
